import { FC, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button, FileViewer, styled } from "@monolith-forensics/monolith-ui";
import { FileIcon, TrashIcon, UploadIcon, XIcon } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import Loader from "@/components/Loader";
import { usePermissions } from "@/hooks/usePermissions";
import { convertBytesToSize } from "@/utils/convertBytesToSize";
import { monolithMoment } from "@/utils/date-format";
import { toast } from "@/utils/toastService";

import {
  useAssetAttachments,
  useUploadAssetAttachment,
} from "../asset-attachments.query";
import type { AssetAttachment } from "../asset-attachments.types";
import { DeleteAssetAttachmentModal } from "./DeleteAssetAttachmentModal";
import { FileList, FileRow } from "./FileRow";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0px;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

// Look from ValidationDocumentsField's DropZone.
const DropZone = styled.div<{ $active: boolean }>`
  border: 2px dashed
    ${({ theme, $active }) =>
      $active ? theme.palette.primary.main : theme.palette.divider};
  border-radius: 6px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  outline: none;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme, $active }) =>
    $active ? theme.palette.primary.main : theme.palette.text.secondary};
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;

  .file-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .upload-error {
    color: ${({ theme }) => theme.palette.error.main};
  }
`;

type PendingUpload = {
  id: string;
  name: string;
  percent: number;
  error?: string;
};

/**
 * The Attachments tab on the asset details page (ENG-1671): the asset's
 * general files, validation reports excluded. Each dropped file uploads on
 * its own (create → PUT → complete); a finished file leaves the pending list
 * and shows up through the list refetch, a failed one stays with its error
 * until dismissed. Rows open in mfui's FileViewer with `file_url` as signed,
 * same as ValidationReports. The caller gates the whole section on
 * ASSET_ATTACHMENT_READ_ALL.
 */
export const AssetAttachmentsSection: FC<{ assetUuid: string }> = ({
  assetUuid,
}) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const canCreate = hasPermission(MONOLITH_PERMISSIONS.ASSET_ATTACHMENT_CREATE);
  const canDelete = hasPermission(MONOLITH_PERMISSIONS.ASSET_ATTACHMENT_DELETE);

  const { data, isLoading, isError } = useAssetAttachments(assetUuid);
  const { mutateAsync: upload } = useUploadAssetAttachment(assetUuid);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [selected, setSelected] = useState<AssetAttachment | null>(null);
  const [toDelete, setToDelete] = useState<AssetAttachment | null>(null);
  // Uploads outlive the tab (switching tabs unmounts this section); a failure
  // that lands after that has no row to show on, so it toasts instead.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const patch = (id: string, change: Partial<PendingUpload>) =>
    setPending((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...change } : p))
    );
  const remove = (id: string) =>
    setPending((prev) => prev.filter((p) => p.id !== id));

  const handleDrop = (files: File[]) => {
    const added = files.map((file) => ({
      file,
      id: uuidv4(),
      name: file.name,
      percent: 0,
    }));
    setPending((prev) => [
      ...prev,
      ...added.map(({ file, ...entry }) => entry),
    ]);
    // ponytail: every file starts at once; the browser caps parallel PUTs
    // per host. Chunk like CreateValidationForm if huge drops become common.
    added.forEach(({ file, id }) =>
      upload({ file, onUploadProgress: (percent) => patch(id, { percent }) })
        .then(() => remove(id))
        .catch((error: Error) =>
          mounted.current
            ? patch(id, { error: error.message })
            : toast.error(`${file.name}: ${error.message}`)
        )
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: true,
    disabled: !canCreate,
  });

  return (
    <Section aria-label="Attachments">
      {canCreate && (
        <DropZone
          {...getRootProps({ role: "button", "aria-label": "Upload files" })}
          $active={isDragActive}
        >
          <input {...getInputProps()} />
          <UploadIcon size={22} strokeWidth={1.5} />
          {isDragActive ? "Drop to upload" : "Drop files or click to select"}
        </DropZone>
      )}
      {pending.length > 0 && (
        <FileList aria-label="Uploads">
          {pending.map((p) => (
            <Row key={p.id}>
              <UploadIcon size={14} />
              <span className="file-name">{p.name}</span>
              {p.error ? (
                <>
                  <span className="upload-error">{p.error}</span>
                  <Button
                    size="xxs"
                    variant="subtle"
                    aria-label={`Dismiss ${p.name}`}
                    leftSection={<XIcon size={12} />}
                    onClick={() => remove(p.id)}
                  />
                </>
              ) : (
                <Muted>{p.percent}%</Muted>
              )}
            </Row>
          ))}
        </FileList>
      )}
      {isLoading ? (
        <Loader />
      ) : isError && !data ? (
        <Muted>Couldn't load attachments.</Muted>
      ) : !data?.length ? (
        <Muted>No attachments</Muted>
      ) : (
        <FileList>
          {data.map((row) => (
            <Row key={row.uuid}>
              <FileRow
                type="button"
                disabled={!row.file_url}
                onClick={() => setSelected(row)}
              >
                <FileIcon size={14} />
                <span className="file-name" title={row.name ?? undefined}>
                  {row.name ?? row.uuid}
                </span>
                {row.size != null && (
                  <span className="file-meta">
                    {convertBytesToSize(row.size)}
                  </span>
                )}
                {row.created_on && (
                  <span className="file-meta">
                    {monolithMoment({ timestamp: row.created_on })}
                  </span>
                )}
              </FileRow>
              {canDelete && (
                <Button
                  size="xs"
                  variant="subtle"
                  color="error"
                  aria-label={`Delete ${row.name ?? row.uuid}`}
                  leftSection={<TrashIcon size={14} />}
                  onClick={() => setToDelete(row)}
                />
              )}
            </Row>
          ))}
        </FileList>
      )}
      <FileViewer
        file={{
          name: selected?.name ?? selected?.uuid ?? "",
          url: selected?.file_url ?? undefined,
          ext: selected?.ext ?? undefined,
          size: selected?.size ?? undefined,
        }}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
      {toDelete && (
        <DeleteAssetAttachmentModal
          show
          onClose={() => setToDelete(null)}
          assetUuid={assetUuid}
          attachment={toDelete}
        />
      )}
    </Section>
  );
};
